// Start times a patient may move an appointment to on one day, from GET /availability.
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const GRID_MINUTES = 30;

const ms = (t) => Date.parse(t);

const toIntervals = (list = []) =>
  list
    .map((x) => ({start: ms(x.start_time), end: ms(x.end_time)}))
    .filter((x) => !Number.isNaN(x.start) && !Number.isNaN(x.end) && x.end > x.start);

const overlaps = (a, b) => a.start < b.end && b.start < a.end;

/** [start, end) of the calendar day `date` (YYYY-MM-DD) in `tz`, as epoch milliseconds. */
export const dayRange = (date, tz) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date || '') || Number.isNaN(Date.parse(date))) {
    return {start: NaN, end: NaN};
  }
  const start = dayjs.tz(`${date}T00:00`, tz);
  return {start: start.valueOf(), end: start.add(1, 'day').valueOf()};
};

/** Local wall-clock HH:mm of an instant in `tz`. */
export const localTime = (instant, tz) => dayjs(instant).tz(tz).format('HH:mm');

/** Local date YYYY-MM-DD of an instant in `tz`. */
export const localDate = (instant, tz) => dayjs(instant).tz(tz).format('YYYY-MM-DD');

/** The instant (ISO) of wall-clock `date` + `time` in `tz`. */
export const toInstant = (date, time, tz) => dayjs.tz(`${date}T${time}`, tz).toISOString();

/**
 * Candidate starts for one doctor: its free slots when it has any that day; none when it has
 * open windows that day but every slot is gone; otherwise (no schedule published) a 30-minute
 * grid, since the API then only rejects overlapping bookings.
 */
const doctorStarts = (doc, day) => {
  const free = toIntervals(doc.free_slots).filter((s) => s.start >= day.start && s.start < day.end);
  if (free.length) return free.map((s) => s.start);
  const open = toIntervals(doc.open_windows).filter((w) => overlaps(w, day));
  if (open.length) return [];
  const out = [];
  for (let t = day.start; t < day.end; t += GRID_MINUTES * 60000) out.push(t);
  return out;
};

/**
 * Bookable start times on `date` in `tz`, as [{value: 'HH:mm', start: ISO}] sorted by time.
 * Never offers past times or times that overlap an occupied or rejected interval. `ignore`
 * is the appointment's own current interval, which must not block itself. With `doctorId`
 * only that doctor counts; otherwise a time is offered when any doctor could take it. When the
 * response lists no matching doctor, the 30-minute grid (minus past times) is offered.
 */
export const rescheduleOptions = ({
  date,
  tz,
  doctors = [],
  doctorId = null,
  durationMinutes = 30,
  now = Date.now(),
  ignore = null,
}) => {
  if (!date || !tz) return [];
  const day = dayRange(date, tz);
  const own = ignore ? {start: ms(ignore.start_time), end: ms(ignore.end_time)} : null;
  const duration = Math.max(1, durationMinutes) * 60000;
  const listed = doctorId ? doctors.filter((d) => d.doctor_id === doctorId) : doctors;
  // A doctor (or calendar entry) without published availability: only overlaps are rejected.
  const pool = listed.length ? listed : [{doctor_id: doctorId}];
  const starts = new Map();
  for (const doc of pool) {
    const busy = [...toIntervals(doc.occupied_slots), ...toIntervals(doc.reject_windows)].filter(
      (b) => !(own && b.start === own.start && b.end === own.end),
    );
    for (const t of doctorStarts(doc, day)) {
      if (t <= now) continue;
      const slot = {start: t, end: t + duration};
      if (busy.some((b) => overlaps(b, slot))) continue;
      const value = localTime(t, tz);
      if (!starts.has(value)) starts.set(value, new Date(t).toISOString());
    }
  }
  return [...starts.entries()]
    .map(([value, start]) => ({value, start}))
    .sort((a, b) => ms(a.start) - ms(b.start));
};
